// The interviewer introduces herself, before the first question.
//
// WHY THIS SCREEN EXISTS
// An interview that opens with a question is a form. A candidate who has not
// been told who is asking, how long it takes, or what happens to the recording
// spends their first two answers working that out — and those answers are
// scored exactly like the later ones.
//
// It also makes the automation honest. Hearing "I'm Maya, I run the first-round
// interviews here" states plainly that this is a system, which a disembodied
// voice reading questions does not.
//
// WHY IT WAITS FOR A CLICK
// The candidate controls when the interview begins. Auto-advancing into question
// one the moment the greeting finishes would start the clock on someone who was
// still settling into their chair — and this is the last moment before anything
// they say is recorded and scored.

import React, { useEffect, useRef } from "react";
import { Box, Button, Typography, Stack, CircularProgress } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InterviewerOrb from "./InterviewerOrb";

const InterviewerGreeting = ({ interviewer, speech, onBegin }) => {
  const spokenRef = useRef(false);

  // Speaks once. Guarded by a ref rather than a dependency list because a
  // re-render for any unrelated reason would otherwise restart the greeting
  // over itself.
  useEffect(() => {
    if (spokenRef.current) return;
    if (!interviewer?.spoken_greeting) return;
    spokenRef.current = true;
    // Index 0 is the greeting on the voice endpoint.
    speech?.speak(interviewer.spoken_greeting, 0);
  }, [interviewer, speech]);

  const name = interviewer?.name || "your interviewer";
  const paragraphs = String(interviewer?.greeting || "")
    .split("\n")
    .filter((line) => line.trim());

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#070d14",
        color: "#e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2.5, md: 4 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 940 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 3, md: 5 }}
          alignItems="center"
        >
          <Box
            sx={{
              width: { xs: 180, md: 260 },
              height: { xs: 180, md: 260 },
              flexShrink: 0,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <InterviewerOrb
              // "thinking" while her greeting clip is still being fetched: the
              // welcome is the one clip personal to this candidate, so it is the
              // only one that can miss the cache and take a beat to arrive.
              state={
                speech?.speaking
                  ? "speaking"
                  : speech?.loading
                    ? "thinking"
                    : "asking"
              }
              label={name}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                letterSpacing: 1.4,
                fontWeight: 700,
                color: "#5eead4",
                mb: 1,
              }}
            >
              YOUR INTERVIEWER
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 28, md: 34 },
                fontWeight: 700,
                letterSpacing: -0.5,
                mb: 2.5,
              }}
            >
              {name}
            </Typography>

            {paragraphs.map((line, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: { xs: 14.5, md: 15.5 },
                  lineHeight: 1.75,
                  color: "rgba(226,232,240,.82)",
                  mb: 1.75,
                  maxWidth: "58ch",
                }}
              >
                {line}
              </Typography>
            ))}

            {/* ── How this actually works ──────────────────────────────────
                The last screen before the questions start, and the only place
                the mechanics can be explained without costing anybody an
                answer.

                It is here because the two most common failures were both
                people not knowing what the screen expected of them: forgetting
                to press record and talking to a camera that was not recording,
                and pressing record then stop by reflex, which used to advance
                the interview and lose the question.

                Three lines, stated as facts about what will happen — not tips.
                Somebody who reads only the bold words still gets it right. */}
            <Box
              sx={{
                mt: 3,
                p: { xs: 2, md: 2.5 },
                borderRadius: 2.5,
                bgcolor: "rgba(148,163,184,.07)",
                border: "1px solid rgba(148,163,184,.16)",
                maxWidth: "58ch",
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: "rgba(226,232,240,.55)",
                  mb: 1.25,
                }}
              >
                How each question works
              </Typography>
              {[
                ["I'll read the question out loud.", "You can replay it any time."],
                [
                  "Recording starts on its own after a short countdown.",
                  "You don't need to press anything — but you can start early, or ask for more time.",
                ],
                [
                  "Press done when you've finished answering.",
                  "If you stop by accident, you'll be asked before anything is sent.",
                ],
              ].map(([bold, rest], i) => (
                <Stack key={i} direction="row" spacing={1.5} sx={{ mb: i === 2 ? 0 : 1.25 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#2dd4bf",
                      minWidth: 16,
                      lineHeight: 1.65,
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13.5, lineHeight: 1.65, color: "rgba(226,232,240,.82)" }}
                  >
                    <Box component="span" sx={{ fontWeight: 700, color: "#e2e8f0" }}>
                      {bold}
                    </Box>{" "}
                    {rest}
                  </Typography>
                </Stack>
              ))}
              <Typography
                sx={{
                  mt: 1.75,
                  pt: 1.5,
                  borderTop: "1px solid rgba(148,163,184,.14)",
                  fontSize: 13,
                  lineHeight: 1.65,
                  color: "rgba(226,232,240,.66)",
                }}
              >
                Don't know an answer? Skip the question rather than recording
                silence — it's treated as unanswered either way, and skipping is
                clearer for everyone.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 3.5 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={onBegin}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 15.5,
                  borderRadius: 2.5,
                  px: 3.5,
                  py: 1.35,
                  bgcolor: "#0b8a8f",
                  "&:hover": { bgcolor: "#0a7a7d" },
                }}
              >
                I'm ready — start the interview
              </Button>

              {speech?.supported && (
                <Button
                  onClick={() => speech.setEnabled(!speech.enabled)}
                  startIcon={
                    speech.enabled ? (
                      <VolumeUpIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <VolumeOffIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 14,
                    borderRadius: 2.5,
                    color: speech.enabled ? "#5eead4" : "#64748b",
                  }}
                >
                  {speech.enabled ? "Voice on" : "Voice off"}
                </Button>
              )}

              {speech?.loading && (
                <CircularProgress size={16} sx={{ color: "#5eead4" }} />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default InterviewerGreeting;
